/**
 * Clinic Redux Slice
 * Manages clinic state
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ClinicService } from '../../services/clinic.service';
import type {
  ClinicProfile,
  ClinicOnboardingData,
  ClinicStatus,
  ClinicDoctorAssociation,
  ClinicStaffAssociation,
  ClinicStats,
  ClinicActivity,
  ClinicDoctorStatus,
  ClinicDoctorRole,
} from '../../types/clinic.types';
import { ClinicStaffStatus } from '../../types/clinic.types';

/**
 * Clinic State Interface
 */
interface ClinicState {
  clinic: ClinicProfile | null;
  stats: ClinicStats | null;
  doctors: ClinicDoctorAssociation[];
  doctorsTotal: number;
  staff: ClinicStaffAssociation[];
  staffTotal: number;
  activities: ClinicActivity[];
  activitiesTotal: number;
  isLoading: boolean;
  error: string | null;
  currentOnboardingStep: number;
}

/**
 * Initial State
 */
const initialState: ClinicState = {
  clinic: null,
  stats: null,
  doctors: [],
  doctorsTotal: 0,
  staff: [],
  staffTotal: 0,
  activities: [],
  activitiesTotal: 0,
  isLoading: false,
  error: null,
  currentOnboardingStep: 1,
};

/**
 * Async Thunks
 */

// Get current clinic
export const getMeAsync = createAsyncThunk<
  ClinicProfile,
  void,
  { rejectValue: string }
>(
  'clinic/getMe',
  async (_, { rejectWithValue }) => {
    try {
      const clinic = await ClinicService.getMe();
      return clinic;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch clinic information.'
      );
    }
  }
);

// Get clinic by ID
export const getByIdAsync = createAsyncThunk<
  ClinicProfile,
  string,
  { rejectValue: string }
>(
  'clinic/getById',
  async (id, { rejectWithValue }) => {
    try {
      const clinic = await ClinicService.getById(id);
      return clinic;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch clinic information.'
      );
    }
  }
);

// Update clinic status
export const updateStatusAsync = createAsyncThunk<
  ClinicProfile,
  ClinicStatus,
  { rejectValue: string }
>(
  'clinic/updateStatus',
  async (status, { rejectWithValue }) => {
    try {
      const clinic = await ClinicService.updateStatus(status);
      return clinic;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update clinic status.'
      );
    }
  }
);

// Onboarding Steps

// Step 1: Update clinic info
export const updateClinicInfoAsync = createAsyncThunk<
  ClinicProfile,
  Partial<ClinicOnboardingData>,
  { rejectValue: string }
>(
  'clinic/updateClinicInfo',
  async (data, { rejectWithValue }) => {
    try {
      const clinic = await ClinicService.updateClinicInfo(data);
      return clinic;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update clinic information.'
      );
    }
  }
);

// Step 2: Update primary address
export const updatePrimaryAddressAsync = createAsyncThunk<
  ClinicProfile,
  Partial<ClinicOnboardingData>,
  { rejectValue: string }
>(
  'clinic/updatePrimaryAddress',
  async (data, { rejectWithValue }) => {
    try {
      const clinic = await ClinicService.updatePrimaryAddress(data);
      return clinic;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update primary address.'
      );
    }
  }
);

// Step 3: Update contact person
export const updateContactPersonAsync = createAsyncThunk<
  ClinicProfile,
  Partial<ClinicOnboardingData>,
  { rejectValue: string }
>(
  'clinic/updateContactPerson',
  async (data, { rejectWithValue }) => {
    try {
      const clinic = await ClinicService.updateContactPerson(data);
      return clinic;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update contact person.'
      );
    }
  }
);

// Step 4: Update licensing
export const updateLicensingAsync = createAsyncThunk<
  ClinicProfile,
  Partial<ClinicOnboardingData>,
  { rejectValue: string }
>(
  'clinic/updateLicensing',
  async (data, { rejectWithValue }) => {
    try {
      const clinic = await ClinicService.updateLicensing(data);
      return clinic;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update licensing information.'
      );
    }
  }
);

// Step 5: Update services provided
export const updateServicesProvidedAsync = createAsyncThunk<
  ClinicProfile,
  Partial<ClinicOnboardingData>,
  { rejectValue: string }
>(
  'clinic/updateServicesProvided',
  async (data, { rejectWithValue }) => {
    try {
      const clinic = await ClinicService.updateServicesProvided(data);
      return clinic;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update services provided.'
      );
    }
  }
);

// Step 6: Update operations
export const updateOperationsAsync = createAsyncThunk<
  ClinicProfile,
  Partial<ClinicOnboardingData>,
  { rejectValue: string }
>(
  'clinic/updateOperations',
  async (data, { rejectWithValue }) => {
    try {
      const clinic = await ClinicService.updateOperations(data);
      return clinic;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update operations schedule.'
      );
    }
  }
);

// Step 7: Update insurance billing
export const updateInsuranceBillingAsync = createAsyncThunk<
  ClinicProfile,
  Partial<ClinicOnboardingData>,
  { rejectValue: string }
>(
  'clinic/updateInsuranceBilling',
  async (data, { rejectWithValue }) => {
    try {
      const clinic = await ClinicService.updateInsuranceBilling(data);
      return clinic;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update insurance billing.'
      );
    }
  }
);

// Step 8: Upload documents
interface DocumentMetadata {
  fileName: string;
  bucket: string;
  key: string;
  baseUrl: string;
  cdnUrl: string;
  contentType: string;
  fileSize: number;
  s3Url: string;
}

export const uploadDocumentsAsync = createAsyncThunk<
  ClinicProfile,
  {
    logo?: DocumentMetadata;
    businessLicense?: DocumentMetadata;
    w9Form?: DocumentMetadata;
    cliaCertificate?: DocumentMetadata;
    addressProof?: DocumentMetadata;
    optionalCertificates?: DocumentMetadata[];
    facilityPhotos?: DocumentMetadata[];
  },
  { rejectValue: string }
>(
  'clinic/uploadDocuments',
  async (data, { rejectWithValue }) => {
    try {
      console.log('Redux Slice - uploadDocumentsAsync received data:', data);
      console.log('Redux Slice - Data keys:', Object.keys(data || {}));
      console.log('Redux Slice - Data JSON:', JSON.stringify(data, null, 2));
      
      const clinic = await ClinicService.uploadDocuments(data);
      
      console.log('Redux Slice - uploadDocuments API response:', clinic);
      return clinic;
    } catch (error: any) {
      console.error('Redux Slice - uploadDocuments API error:', error);
      return rejectWithValue(
        error.response?.data?.message || 'Failed to upload documents.'
      );
    }
  }
);

// Step 9: Update terms compliance
export const updateTermsComplianceAsync = createAsyncThunk<
  ClinicProfile,
  Partial<ClinicOnboardingData>,
  { rejectValue: string }
>(
  'clinic/updateTermsCompliance',
  async (data, { rejectWithValue }) => {
    try {
      const clinic = await ClinicService.updateTermsCompliance(data);
      return clinic;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update terms and compliance.'
      );
    }
  }
);

// Step 10: Review and submit
export const reviewSubmitAsync = createAsyncThunk<
  ClinicProfile,
  Partial<ClinicOnboardingData> | undefined,
  { rejectValue: string }
>(
  'clinic/reviewSubmit',
  async (data, { rejectWithValue }) => {
    try {
      const clinic = await ClinicService.reviewSubmit(data);
      return clinic;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to submit review.'
      );
    }
  }
);

// Get clinic stats
export const getStatsAsync = createAsyncThunk<
  ClinicStats,
  string | undefined,
  { rejectValue: string }
>(
  'clinic/getStats',
  async (clinicId, { rejectWithValue }) => {
    try {
      const stats = await ClinicService.getStats(clinicId);
      return stats;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch clinic statistics.'
      );
    }
  }
);

// Get doctors
export const getDoctorsAsync = createAsyncThunk<
  { doctors: ClinicDoctorAssociation[]; total: number },
  {
    status?: ClinicDoctorStatus;
    role?: ClinicDoctorRole;
    page?: number;
    limit?: number;
  } | undefined,
  { rejectValue: string }
>(
  'clinic/getDoctors',
  async (params, { rejectWithValue }) => {
    try {
      const response = await ClinicService.getDoctors(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch doctors.'
      );
    }
  }
);

// Get doctor
export const getDoctorAsync = createAsyncThunk<
  ClinicDoctorAssociation,
  string,
  { rejectValue: string }
>(
  'clinic/getDoctor',
  async (doctorId, { rejectWithValue }) => {
    try {
      const doctor = await ClinicService.getDoctor(doctorId);
      return doctor;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch doctor information.'
      );
    }
  }
);

// Invite doctor
export const inviteDoctorAsync = createAsyncThunk<
  { message: string; invitationId: string },
  {
    email: string;
    role?: ClinicDoctorRole;
    message?: string;
  },
  { rejectValue: string }
>(
  'clinic/inviteDoctor',
  async (data, { rejectWithValue }) => {
    try {
      const response = await ClinicService.inviteDoctor(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to invite doctor.'
      );
    }
  }
);

// Approve doctor
export const approveDoctorAsync = createAsyncThunk<
  ClinicDoctorAssociation,
  { doctorId: string; role?: ClinicDoctorRole },
  { rejectValue: string }
>(
  'clinic/approveDoctor',
  async ({ doctorId, role }, { rejectWithValue }) => {
    try {
      const doctor = await ClinicService.approveDoctor(doctorId, role);
      return doctor;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to approve doctor.'
      );
    }
  }
);

// Reject doctor
export const rejectDoctorAsync = createAsyncThunk<
  { message: string },
  { doctorId: string; reason?: string },
  { rejectValue: string }
>(
  'clinic/rejectDoctor',
  async ({ doctorId, reason }, { rejectWithValue }) => {
    try {
      const response = await ClinicService.rejectDoctor(doctorId, reason);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to reject doctor.'
      );
    }
  }
);

// Update doctor
export const updateDoctorAsync = createAsyncThunk<
  ClinicDoctorAssociation,
  {
    doctorId: string;
    data: {
      role?: ClinicDoctorRole;
      status?: ClinicDoctorStatus;
    };
  },
  { rejectValue: string }
>(
  'clinic/updateDoctor',
  async ({ doctorId, data }, { rejectWithValue }) => {
    try {
      const doctor = await ClinicService.updateDoctor(doctorId, data);
      return doctor;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update doctor.'
      );
    }
  }
);

// Remove doctor
export const removeDoctorAsync = createAsyncThunk<
  { message: string },
  { doctorId: string; reason?: string },
  { rejectValue: string }
>(
  'clinic/removeDoctor',
  async ({ doctorId, reason }, { rejectWithValue }) => {
    try {
      const response = await ClinicService.removeDoctor(doctorId, reason);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to remove doctor.'
      );
    }
  }
);

// Get activities
export const getActivitiesAsync = createAsyncThunk<
  { activities: ClinicActivity[]; total: number },
  {
    type?: string;
    doctorId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  } | undefined,
  { rejectValue: string }
>(
  'clinic/getActivities',
  async (params, { rejectWithValue }) => {
    try {
      const response = await ClinicService.getActivities(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch activities.'
      );
    }
  }
);

// Request to join
export const requestToJoinAsync = createAsyncThunk<
  { message: string },
  { clinicId: string; message?: string },
  { rejectValue: string }
>(
  'clinic/requestToJoin',
  async ({ clinicId, message }, { rejectWithValue }) => {
    try {
      const response = await ClinicService.requestToJoin(clinicId, message);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to send join request.'
      );
    }
  }
);

// Get staff
export const getStaffAsync = createAsyncThunk<
  { staff: ClinicStaffAssociation[]; total: number },
  {
    status?: ClinicStaffStatus;
    role?: 'doctor' | 'receptionist' | 'admin';
    page?: number;
    limit?: number;
  } | undefined,
  { rejectValue: string }
>(
  'clinic/getStaff',
  async (params, { rejectWithValue }) => {
    try {
      // Map params to API format
      const apiParams: {
        status?: 'ACTIVE' | 'INACTIVE';
        role?: 'DOCTOR' | 'NURSE' | 'RECEPTIONIST';
        page?: number;
        limit?: number;
      } = {};

      if (params) {
        if (params.page) apiParams.page = params.page;
        if (params.limit) apiParams.limit = params.limit;

        // Map role to API format
        if (params.role) {
          const roleMap: Record<string, 'DOCTOR' | 'RECEPTIONIST'> = {
            doctor: 'DOCTOR',
            receptionist: 'RECEPTIONIST',
          };
          if (roleMap[params.role]) {
            apiParams.role = roleMap[params.role];
          }
        }

        // Map status to API format
        if (params.status) {
          const statusMap: Record<ClinicStaffStatus, 'ACTIVE' | 'INACTIVE'> = {
            [ClinicStaffStatus.ACTIVE]: 'ACTIVE',
            [ClinicStaffStatus.INACTIVE]: 'INACTIVE',
            [ClinicStaffStatus.PENDING]: 'INACTIVE', // Map pending to inactive for API
            [ClinicStaffStatus.SUSPENDED]: 'INACTIVE', // Map suspended to inactive for API
            [ClinicStaffStatus.LEFT]: 'INACTIVE', // Map left to inactive for API
          };
          if (statusMap[params.status]) {
            apiParams.status = statusMap[params.status];
          }
        }
      }

      const response = await ClinicService.getStaff(apiParams);
      
      // Map API response to expected format
      const mappedStaff: ClinicStaffAssociation[] = response.staff.map((apiStaff: any) => {
        const roleMap: Record<string, 'doctor' | 'receptionist' | 'admin'> = {
          DOCTOR: 'doctor',
          RECEPTIONIST: 'receptionist',
        };

        const statusMap: Record<string, ClinicStaffStatus> = {
          ACTIVE: ClinicStaffStatus.ACTIVE,
          INACTIVE: ClinicStaffStatus.INACTIVE,
        };

        return {
          id: apiStaff._id,
          clinicId: apiStaff.clinicId,
          userId: apiStaff._id,
          status: statusMap[apiStaff.status] || ClinicStaffStatus.INACTIVE,
          role: roleMap[apiStaff.role] || 'doctor',
          joinedAt: apiStaff.createdAt || new Date().toISOString(),
          approvedAt: apiStaff.onboarding?.approvedAt,
          approvedBy: apiStaff.onboarding?.approvedBy,
          user: {
            id: apiStaff._id,
            firstName: apiStaff.basicInfo?.firstName || '',
            lastName: apiStaff.basicInfo?.lastName || '',
            email: apiStaff.basicInfo?.email || '',
            avatar: apiStaff.basicInfo?.profilePhoto?.cdnUrl || apiStaff.basicInfo?.profilePhoto?.baseUrl,
            phone: apiStaff.basicInfo?.phone || '',
            isActive: apiStaff.status === 'ACTIVE',
          },
          profile: {
            basicInfo: {
              firstName: apiStaff.basicInfo?.firstName,
              lastName: apiStaff.basicInfo?.lastName,
              email: apiStaff.basicInfo?.email,
              phone: apiStaff.basicInfo?.phone,
              dateOfBirth: apiStaff.basicInfo?.dob,
              gender: apiStaff.basicInfo?.gender,
              photo: apiStaff.basicInfo?.profilePhoto?.cdnUrl || apiStaff.basicInfo?.profilePhoto?.baseUrl,
              address: apiStaff.basicInfo?.address ? {
                street: apiStaff.basicInfo.address.street,
                city: apiStaff.basicInfo.address.city,
                state: apiStaff.basicInfo.address.state,
                zipCode: apiStaff.basicInfo.address.zip,
              } : undefined,
            },
          },
        };
      });

      return {
        staff: mappedStaff,
        total: response.pagination.total,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch staff.'
      );
    }
  }
);

// Get staff member
export const getStaffMemberAsync = createAsyncThunk<
  ClinicStaffAssociation,
  string,
  { rejectValue: string }
>(
  'clinic/getStaffMember',
  async (userId, { rejectWithValue }) => {
    try {
      const staff = await ClinicService.getStaffMember(userId);
      return staff;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch staff member.'
      );
    }
  }
);

// Invite staff
export const inviteStaffAsync = createAsyncThunk<
  { message: string; invitationId: string },
  {
    email: string;
    role: 'doctor' | 'receptionist' | 'admin';
    message?: string;
  },
  { rejectValue: string }
>(
  'clinic/inviteStaff',
  async (data, { rejectWithValue }) => {
    try {
      const response = await ClinicService.inviteStaff(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to invite staff.'
      );
    }
  }
);

// Update staff
export const updateStaffAsync = createAsyncThunk<
  ClinicStaffAssociation,
  {
    userId: string;
    data: {
      role?: 'doctor' | 'receptionist' | 'admin';
      status?: ClinicStaffStatus;
    };
  },
  { rejectValue: string }
>(
  'clinic/updateStaff',
  async ({ userId, data }, { rejectWithValue }) => {
    try {
      const staff = await ClinicService.updateStaff(userId, data);
      return staff;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update staff.'
      );
    }
  }
);

// Remove staff
export const removeStaffAsync = createAsyncThunk<
  { message: string },
  { userId: string; reason?: string },
  { rejectValue: string }
>(
  'clinic/removeStaff',
  async ({ userId, reason }, { rejectWithValue }) => {
    try {
      const response = await ClinicService.removeStaff(userId, reason);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to remove staff.'
      );
    }
  }
);

// Staff Management Async Thunks

// Create staff
export const createStaffAsync = createAsyncThunk<
  ClinicStaffAssociation,
  {
    email: string;
    role: 'doctor' | 'receptionist' | 'admin';
    firstName?: string;
    lastName?: string;
    phone?: string;
  },
  { rejectValue: string }
>(
  'clinic/createStaff',
  async (data, { rejectWithValue }) => {
    try {
      const staff = await ClinicService.createStaff(data);
      return staff;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to create staff member.'
      );
    }
  }
);

// Create doctor (POST /staff/doctor)
export const createDoctorAsync = createAsyncThunk<
  { staff: any },
  {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    password: string;
    dob: string;
    gender: string;
    npiNumber?: string;
    basicInfo?: any;
    basicProfessionalInfo?: any;
    address?: any;
    profilePhoto?: any;
  },
  { rejectValue: string }
>(
  'clinic/createDoctor',
  async (data, { rejectWithValue }) => {
    try {
      const response = await ClinicService.createDoctor(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to create doctor.'
      );
    }
  }
);

// Create receptionist (POST /staff/receptionist)
export const createReceptionistAsync = createAsyncThunk<
  { staff: any },
  {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    password: string;
    dob: string;
    gender: string;
    isPublishable?: boolean;
    providerType?: string;
    displayName?: string;
    basicInfo: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
      dob: string;
      gender: string;
      profilePhoto?: {
        cdnUrl?: string;
        baseUrl?: string;
        s3Url?: string;
      };
      address?: {
        street?: string;
        city?: string;
        state?: string;
        zip?: string;
      };
    };
  },
  { rejectValue: string }
>(
  'clinic/createReceptionist',
  async (data, { rejectWithValue }) => {
    try {
      const response = await ClinicService.createReceptionist(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to create receptionist.'
      );
    }
  }
);

// Get staff by ID
export const getStaffByIdAsync = createAsyncThunk<
  ClinicStaffAssociation,
  string,
  { rejectValue: string }
>(
  'clinic/getStaffById',
  async (id, { rejectWithValue }) => {
    try {
      const staff = await ClinicService.getStaffById(id);
      return staff;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch staff member.'
      );
    }
  }
);

// Update staff status
export const updateStaffStatusAsync = createAsyncThunk<
  ClinicStaffAssociation,
  { id: string; status: ClinicStaffStatus },
  { rejectValue: string }
>(
  'clinic/updateStaffStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const staff = await ClinicService.updateStaffStatus(id, status);
      return staff;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update staff status.'
      );
    }
  }
);

// Staff Onboarding - Doctor Async Thunks

// Update doctor basic info
export const updateDoctorBasicInfoAsync = createAsyncThunk<
  ClinicStaffAssociation,
  { id: string; data: Partial<ClinicStaffAssociation['profile']> }, // id is Clinic ID
  { rejectValue: string }
>(
  'clinic/updateDoctorBasicInfo',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      console.log(`[updateDoctorBasicInfoAsync] Clinic ID: ${id}`);
      const staff = await ClinicService.updateDoctorBasicInfo(id, data);
      return staff;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update doctor basic info.'
      );
    }
  }
);

// Update doctor credentials
export const updateDoctorCredentialsAsync = createAsyncThunk<
  ClinicStaffAssociation,
  { id: string; data: Partial<ClinicStaffAssociation['profile']> },
  { rejectValue: string }
>(
  'clinic/updateDoctorCredentials',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const staff = await ClinicService.updateDoctorCredentials(id, data);
      return staff;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update doctor credentials.'
      );
    }
  }
);

// Update doctor services
export const updateDoctorServicesAsync = createAsyncThunk<
  ClinicStaffAssociation,
  { id: string; data: Partial<ClinicStaffAssociation['profile']> },
  { rejectValue: string }
>(
  'clinic/updateDoctorServices',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const staff = await ClinicService.updateDoctorServices(id, data);
      return staff;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update doctor services.'
      );
    }
  }
);

// Update doctor pricing
export const updateDoctorPricingAsync = createAsyncThunk<
  ClinicStaffAssociation,
  { id: string; data: Partial<ClinicStaffAssociation['profile']> },
  { rejectValue: string }
>(
  'clinic/updateDoctorPricing',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const staff = await ClinicService.updateDoctorPricing(id, data);
      return staff;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update doctor pricing.'
      );
    }
  }
);

// Update doctor availability
export const updateDoctorAvailabilityAsync = createAsyncThunk<
  ClinicStaffAssociation,
  { id: string; data: Partial<ClinicStaffAssociation['profile']> },
  { rejectValue: string }
>(
  'clinic/updateDoctorAvailability',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const staff = await ClinicService.updateDoctorAvailability(id, data);
      return staff;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update doctor availability.'
      );
    }
  }
);

// Update doctor payment
export const updateDoctorPaymentAsync = createAsyncThunk<
  ClinicStaffAssociation,
  { id: string; data: Partial<ClinicStaffAssociation['profile']> },
  { rejectValue: string }
>(
  'clinic/updateDoctorPayment',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const staff = await ClinicService.updateDoctorPayment(id, data);
      return staff;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update doctor payment info.'
      );
    }
  }
);

// Submit doctor profile
export const submitDoctorProfileAsync = createAsyncThunk<
  ClinicStaffAssociation,
  { id: string; data?: Partial<ClinicStaffAssociation['profile']> },
  { rejectValue: string }
>(
  'clinic/submitDoctorProfile',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const staff = await ClinicService.submitDoctorProfile(id, data);
      return staff;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to submit doctor profile.'
      );
    }
  }
);

// Staff Onboarding - Receptionist Async Thunks

// Update receptionist basic info
export const updateReceptionistBasicInfoAsync = createAsyncThunk<
  ClinicStaffAssociation,
  { id: string; data: Partial<ClinicStaffAssociation['profile']> },
  { rejectValue: string }
>(
  'clinic/updateReceptionistBasicInfo',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const staff = await ClinicService.updateReceptionistBasicInfo(id, data);
      return staff;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update receptionist basic info.'
      );
    }
  }
);

// Update receptionist credentials
export const updateReceptionistCredentialsAsync = createAsyncThunk<
  ClinicStaffAssociation,
  { id: string; data: Partial<ClinicStaffAssociation['profile']> },
  { rejectValue: string }
>(
  'clinic/updateReceptionistCredentials',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const staff = await ClinicService.updateReceptionistCredentials(id, data);
      return staff;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update receptionist credentials.'
      );
    }
  }
);

// Update receptionist schedule
export const updateReceptionistScheduleAsync = createAsyncThunk<
  ClinicStaffAssociation,
  { id: string; data: Partial<ClinicStaffAssociation['profile']> },
  { rejectValue: string }
>(
  'clinic/updateReceptionistSchedule',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const staff = await ClinicService.updateReceptionistSchedule(id, data);
      return staff;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update receptionist schedule.'
      );
    }
  }
);

// Update receptionist permissions
export const updateReceptionistPermissionsAsync = createAsyncThunk<
  ClinicStaffAssociation,
  { id: string; data: Partial<ClinicStaffAssociation['profile']> },
  { rejectValue: string }
>(
  'clinic/updateReceptionistPermissions',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const staff = await ClinicService.updateReceptionistPermissions(id, data);
      return staff;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update receptionist permissions.'
      );
    }
  }
);

// Submit receptionist profile
export const submitReceptionistProfileAsync = createAsyncThunk<
  ClinicStaffAssociation,
  { id: string; data?: Partial<ClinicStaffAssociation['profile']> },
  { rejectValue: string }
>(
  'clinic/submitReceptionistProfile',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const staff = await ClinicService.submitReceptionistProfile(id, data);
      return staff;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to submit receptionist profile.'
      );
    }
  }
);

/**
 * Clinic Slice
 */
const clinicSlice = createSlice({
  name: 'clinic',
  initialState,
  reducers: {
    // Set clinic manually
    setClinic: (state, action: PayloadAction<ClinicProfile>) => {
      state.clinic = action.payload;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Clear clinic state
    clearClinic: (state) => {
      state.clinic = null;
      state.stats = null;
      state.doctors = [];
      state.doctorsTotal = 0;
      state.staff = [];
      state.staffTotal = 0;
      state.activities = [];
      state.activitiesTotal = 0;
      state.error = null;
      state.currentOnboardingStep = 1;
    },

    // Update clinic data
    updateClinic: (state, action: PayloadAction<Partial<ClinicProfile>>) => {
      if (state.clinic) {
        state.clinic = { ...state.clinic, ...action.payload };
      }
    },

    // Set current onboarding step
    setOnboardingStep: (state, action: PayloadAction<number>) => {
      state.currentOnboardingStep = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Get Me
    builder
      .addCase(getMeAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getMeAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.clinic = action.payload;
        state.error = null;
      })
      .addCase(getMeAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch clinic';
      });

    // Get By ID
    builder
      .addCase(getByIdAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getByIdAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.clinic = action.payload;
        state.error = null;
      })
      .addCase(getByIdAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch clinic';
      });

    // Update Status
    builder
      .addCase(updateStatusAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateStatusAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.clinic = action.payload;
        state.error = null;
      })
      .addCase(updateStatusAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update status';
      });

    // Onboarding Steps - All follow similar pattern
    const onboardingSteps = [
      updateClinicInfoAsync,
      updatePrimaryAddressAsync,
      updateContactPersonAsync,
      updateLicensingAsync,
      updateServicesProvidedAsync,
      updateOperationsAsync,
      updateInsuranceBillingAsync,
      uploadDocumentsAsync,
      updateTermsComplianceAsync,
      reviewSubmitAsync,
    ];

    onboardingSteps.forEach((asyncThunk) => {
      builder
        .addCase(asyncThunk.pending, (state) => {
          state.isLoading = true;
          state.error = null;
        })
        .addCase(asyncThunk.fulfilled, (state, action) => {
          state.isLoading = false;
          state.clinic = action.payload;
          state.error = null;
        })
        .addCase(asyncThunk.rejected, (state, action) => {
          state.isLoading = false;
          state.error = action.payload || 'Operation failed';
        });
    });

    // Get Stats
    builder
      .addCase(getStatsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getStatsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stats = action.payload;
        state.error = null;
      })
      .addCase(getStatsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch stats';
      });

    // Get Doctors
    builder
      .addCase(getDoctorsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDoctorsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.doctors = action.payload.doctors;
        state.doctorsTotal = action.payload.total;
        state.error = null;
      })
      .addCase(getDoctorsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch doctors';
      });

    // Approve Doctor
    builder
      .addCase(approveDoctorAsync.fulfilled, (state, action) => {
        const index = state.doctors.findIndex(
          (d) => d.doctorId === action.payload.doctorId
        );
        if (index !== -1) {
          state.doctors[index] = action.payload;
        } else {
          state.doctors.push(action.payload);
        }
      });

    // Reject/Remove Doctor
    builder
      .addCase(rejectDoctorAsync.fulfilled, (state, action) => {
        // Remove from list or update status
        state.doctors = state.doctors.filter(
          (d) => d.doctorId !== action.meta.arg.doctorId
        );
      })
      .addCase(removeDoctorAsync.fulfilled, (state, action) => {
        state.doctors = state.doctors.filter(
          (d) => d.doctorId !== action.meta.arg.doctorId
        );
      });

    // Update Doctor
    builder
      .addCase(updateDoctorAsync.fulfilled, (state, action) => {
        const index = state.doctors.findIndex(
          (d) => d.doctorId === action.payload.doctorId
        );
        if (index !== -1) {
          state.doctors[index] = action.payload;
        }
      });

    // Get Activities
    builder
      .addCase(getActivitiesAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getActivitiesAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.activities = action.payload.activities;
        state.activitiesTotal = action.payload.total;
        state.error = null;
      })
      .addCase(getActivitiesAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch activities';
      });

    // Get Staff
    builder
      .addCase(getStaffAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getStaffAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.staff = action.payload.staff;
        state.staffTotal = action.payload.total;
        state.error = null;
      })
      .addCase(getStaffAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Failed to fetch staff';
      });

    // Update Staff
    builder
      .addCase(updateStaffAsync.fulfilled, (state, action) => {
        const index = state.staff.findIndex(
          (s) => s.userId === action.payload.userId
        );
        if (index !== -1) {
          state.staff[index] = action.payload;
        }
      });

    // Remove Staff
    builder
      .addCase(removeStaffAsync.fulfilled, (state, action) => {
        state.staff = state.staff.filter(
          (s) => s.userId !== action.meta.arg.userId
        );
      });

    // Create Staff
    builder
      .addCase(createStaffAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createStaffAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.staff.push(action.payload);
        state.staffTotal += 1;
        state.error = null;
      })
      .addCase(createStaffAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to create staff';
      });

    // Get Staff By ID
    builder
      .addCase(getStaffByIdAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getStaffByIdAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.staff.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.staff[index] = action.payload;
        } else {
          state.staff.push(action.payload);
        }
        state.error = null;
      })
      .addCase(getStaffByIdAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch staff member';
      });

    // Update Staff Status
    builder
      .addCase(updateStaffStatusAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateStaffStatusAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.staff.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.staff[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateStaffStatusAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update staff status';
      });

    // Staff Onboarding - Doctor Steps
    const doctorOnboardingSteps = [
      updateDoctorBasicInfoAsync,
      updateDoctorCredentialsAsync,
      updateDoctorServicesAsync,
      updateDoctorPricingAsync,
      updateDoctorAvailabilityAsync,
      updateDoctorPaymentAsync,
      submitDoctorProfileAsync,
    ];

    doctorOnboardingSteps.forEach((asyncThunk) => {
      builder
        .addCase(asyncThunk.pending, (state) => {
          state.isLoading = true;
          state.error = null;
        })
        .addCase(asyncThunk.fulfilled, (state, action) => {
          state.isLoading = false;
          const index = state.staff.findIndex((s) => s.id === action.payload.id);
          if (index !== -1) {
            state.staff[index] = action.payload;
          } else {
            state.staff.push(action.payload);
          }
          state.error = null;
        })
        .addCase(asyncThunk.rejected, (state, action) => {
          state.isLoading = false;
          state.error = action.payload || 'Operation failed';
        });
    });

    // Staff Onboarding - Receptionist Steps
    const receptionistOnboardingSteps = [
      updateReceptionistBasicInfoAsync,
      updateReceptionistCredentialsAsync,
      updateReceptionistScheduleAsync,
      updateReceptionistPermissionsAsync,
      submitReceptionistProfileAsync,
    ];

    receptionistOnboardingSteps.forEach((asyncThunk) => {
      builder
        .addCase(asyncThunk.pending, (state) => {
          state.isLoading = true;
          state.error = null;
        })
        .addCase(asyncThunk.fulfilled, (state, action) => {
          state.isLoading = false;
          const index = state.staff.findIndex((s) => s.id === action.payload.id);
          if (index !== -1) {
            state.staff[index] = action.payload;
          } else {
            state.staff.push(action.payload);
          }
          state.error = null;
        })
        .addCase(asyncThunk.rejected, (state, action) => {
          state.isLoading = false;
          state.error = action.payload || 'Operation failed';
        });
    });
  },
});

// Actions
export const { setClinic, clearError, clearClinic, updateClinic, setOnboardingStep } =
  clinicSlice.actions;

// Selectors
export const selectClinic = (state: { clinic: ClinicState }) => state.clinic;
export const selectClinicProfile = (state: { clinic: ClinicState }) => state.clinic.clinic;
export const selectClinicStats = (state: { clinic: ClinicState }) => state.clinic.stats;
export const selectClinicDoctors = (state: { clinic: ClinicState }) => state.clinic.doctors;
export const selectClinicStaff = (state: { clinic: ClinicState }) => state.clinic.staff;
export const selectClinicActivities = (state: { clinic: ClinicState }) => state.clinic.activities;
export const selectClinicLoading = (state: { clinic: ClinicState }) => state.clinic.isLoading;
export const selectClinicError = (state: { clinic: ClinicState }) => state.clinic.error;
export const selectCurrentOnboardingStep = (state: { clinic: ClinicState }) =>
  state.clinic.currentOnboardingStep;

// Reducer
export default clinicSlice.reducer;
